import { motion } from "framer-motion";
import type { ExampleOutcome } from "@/lib/types";
import { MonoLabel } from "./primitives";
import { cn } from "@/lib/utils";

/** Home page demonstration card. Data is illustrative by definition. */
export function OutcomeCard({ example, index }: { example: ExampleOutcome; index: number }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      className="panel panel-hover hud-corners scanlines flex h-full flex-col p-6"
      data-testid={`example-card-${example.id.toLowerCase()}`}
    >
      <div className="flex items-center justify-between">
        <MonoLabel className="text-cyan">{example.actorType}</MonoLabel>
        <span className="font-mono text-[10px] text-dim/70">{example.actorId}</span>
      </div>

      <p className="mt-5 font-display text-lg font-semibold leading-snug text-ink">{example.claim}</p>

      <div className="mt-6 flex-1 border-t border-line">
        {example.rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between border-b border-line/70 py-2.5">
            <MonoLabel className="text-[9px]">{r.label}</MonoLabel>
            <span className="font-mono text-sm text-ink">{r.value}</span>
          </div>
        ))}
        <div className="flex items-center justify-between border-b border-line/70 py-2.5">
          <MonoLabel className="text-[9px]">RESULT</MonoLabel>
          <span
            className={cn(
              "font-mono text-xs uppercase tracking-[0.18em]",
              example.resultTone === "success" ? "text-success" : "text-cyan"
            )}
          >
            <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-current align-middle" />
            {example.result}
          </span>
        </div>
      </div>

      <div className="mt-5 flex items-end justify-between gap-4">
        <MonoLabel className="text-[9px]">Consequence</MonoLabel>
        <span
          className={cn(
            "font-display text-2xl font-bold",
            example.resultTone === "success" ? "text-success" : "text-cyan"
          )}
        >
          {example.consequence}
        </span>
      </div>

      <div className="mt-4 border-t border-line/60 pt-3">
        <MonoLabel className="text-[8px] text-dim/60">Demonstration example — not live protocol data</MonoLabel>
      </div>
    </motion.article>
  );
}
